import { Component, signal, OnInit, OnDestroy, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, RouterLinkActive],
  templateUrl: './app.html',
  styleUrl: './app.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('website');
  protected readonly menuOpen = signal(false);
  protected readonly langOpen = signal(false);
  protected currentLocale: string = 'de';
  private readonly supportedLocales = ['de', 'en', 'tr', 'sr-Latn', 'bs'];

  private readonly localeLabels: Record<string, string> = {
    'de': 'Deutsch',
    'en': 'English',
    'tr': 'Türkçe',
    'sr-Latn': 'Srpski',
    'bs': 'Bosanski'
  };

  // Sanfter, performanter Scroll-Handler mit rAF und kleiner Hysterese
  private ticking = false;
  private lastScrollY = 0;

  private updateScrollState = () => {
    const header = document.querySelector('.header') as HTMLElement | null;
    const y = this.lastScrollY;
    if (header) {
      const scrolled = y > 24; // kleine Hysterese gegen Flackern
      header.classList.toggle('scrolled', scrolled);
    }
    // Aktiven Link anhand der sichtbaren Sektion setzen
    this.updateActiveLinkByScroll();
    this.ticking = false;
  };

  private onScroll = () => {
    this.lastScrollY = window.scrollY;
    if (!this.ticking) {
      this.ticking = true;
      requestAnimationFrame(this.updateScrollState);
    }
  };

  ngOnInit() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    // Initialer Zustand setzen
    this.onScroll();

    // Aktuelle Locale aus der URL ableiten (z. B. /de/, /en/ ...)
    this.currentLocale = this.detectLocaleFromUrl() ?? 'de';
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  }
  
  toggleMenu() { 
    this.menuOpen.set(!this.menuOpen()); 
  }

  toggleLang() {
    this.langOpen.set(!this.langOpen());
  }

  closeLang() {
    this.langOpen.set(false);
  }
  
  scrollToSection(sectionId: string) {
    // Schließe das mobile Menü
    this.menuOpen.set(false);
    
    // Scroll zur Sektion
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }

    // Aktiven Link visuell markieren (Fallback für Klick)
    this.setActiveLink(sectionId);
  }

  private updateActiveLinkByScroll() {
    const sectionIds = ['hero', 'ueber-uns', 'dienstleistungen', 'links', 'kontakt'];
    let bestId: string | null = null;
    let bestScore = Number.POSITIVE_INFINITY;

    for (const id of sectionIds) {
      const el = document.getElementById(id);
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      // Score: Nähe zur oberen Kante, leichte Präferenz für im Viewport befindliche Elemente
      const top = rect.top;
      const inViewPenalty = top < 0 ? Math.abs(top) * 0.5 : top; // bevorzugt nahe am oberen Rand
      if (inViewPenalty < bestScore) {
        bestScore = inViewPenalty;
        bestId = id;
      }
    }

    if (bestId) {
      this.setActiveLink(bestId);
    }
  }

  private setActiveLink(sectionId: string) {
    const links = document.querySelectorAll('.nav-links a');
    links.forEach((a) => a.classList.remove('active'));
    const target = document.querySelector(`.nav-links a[href="#${sectionId}"]`);
    if (target) {
      target.classList.add('active');
    }
  }

  private detectLocaleFromUrl(): string | null {
    const path = window.location.pathname; // e.g., /de/abc or /en/ or /
    const first = path.split('/').filter(Boolean)[0];
    if (first && this.supportedLocales.includes(first)) return first;
    return null;
  }

  changeLocale(locale: string) {
    if (!this.supportedLocales.includes(locale)) return;

    // Bestehende Locale im Pfad entfernen, Restpfad erhalten
    const { origin, pathname, search, hash } = window.location;
    const segments = pathname.split('/').filter(Boolean);
    const first = segments[0];
    const hasLocale = first && this.supportedLocales.includes(first);
    const restSegments = hasLocale ? segments.slice(1) : segments;

    // Ziel-URL zusammensetzen: /<locale>/<rest>
    const restPath = restSegments.join('/');
    const newPath = `/${locale}/${restPath ? restPath + '/' : ''}`;

    // In Dev-Serve existieren andere Locales nicht; dennoch Weiterleitung ausführen
    // (funktioniert mit Production-Builds; in Dev pro Locale separat serven)
    window.location.href = `${origin}${newPath}${search}${hash}`;
    this.closeLang();
  }

  get currentLocaleLabel(): string {
    return this.localeLabels[this.currentLocale] ?? this.currentLocale.toUpperCase();
  }
}
